import { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/i18n/index";
import PatientLayout from "@/components/layout/PatientLayout";
import api from "@/api";
import { toast } from "sonner";
import { 
  CheckCircle, ArrowLeft, ArrowRight,
  Thermometer, Heart, Activity, Scale,
  Battery, Zap, Wind, Utensils, CircleDot, Flame, AlertCircle,
  Smile, Meh, Frown, Angry, ClipboardList,
    Droplets, Maximize, ThermometerSun, Home
  } from "lucide-react";
import { PatientSpeakerButton, usePatientAudioCache } from "@/components/shared/PatientSpeakerButton";

// ===========================================
// AUDIO MESSAGES — loaded from i18n at runtime (see getAudioMessages)
// ===========================================

// ===========================================
// SYMPTOM CONFIGURATIONS with grade descriptions — loaded from i18n at runtime
// ===========================================
const SYMPTOMS_KEYS = [
  { key: "fatigue", icon: Battery },
  { key: "pain", icon: AlertCircle },
  { key: "nausea", icon: Frown },
  { key: "diarrhea", icon: Utensils },
  { key: "constipation", icon: Utensils },
  { key: "dyspnea", icon: Wind },
  { key: "appetite", icon: Utensils },
  { key: "neuropathy", icon: Zap },
  { key: "mucositis", icon: CircleDot },
  { key: "rash", icon: Flame },
  { key: "bleeding", icon: Droplets },
  { key: "edema", icon: Maximize },
  { key: "perceivedFever", icon: ThermometerSun },
  { key: "cough", icon: Wind },
  { key: "joint_pain", icon: Activity },
  { key: "hot_flashes", icon: Flame },
  { key: "sleep_issues", icon: Meh },
  { key: "pruritus", icon: Flame },
  { key: "infection_signs", icon: ThermometerSun },
];

// ===========================================
// SEVERITY LEVELS (0-3 scale with colored faces)
// ===========================================
const SEVERITY_LEVELS = [
  { value: 0, icon: Smile, color: "#059669", bgColor: "#d1fae5", label: "0" },
  { value: 1, icon: Meh, color: "#0ea5e9", bgColor: "#e0f2fe", label: "1" },
  { value: 2, icon: Frown, color: "#d97706", bgColor: "#fef3c7", label: "2" },
  { value: 3, icon: Angry, color: "#e11d48", bgColor: "#ffe4e6", label: "3" },
];

// ===========================================
// SEVERITY SELECTOR COMPONENT (4 faces with colors + grade descriptions)
// ===========================================
const SeveritySelector = ({ symptomKey, currentValue, onChange, grades }) => {
  return (
    <div 
      className="grid grid-cols-4 gap-2 sm:gap-3 p-2.5 sm:p-3 rounded-3xl border border-sky-100"
      style={{
        background: 'linear-gradient(to right, #ecfdf5, #f0f9ff, #fffbeb, #fff1f2)'
      }}
    >
      {SEVERITY_LEVELS.map((sev) => {
        const isSelected = currentValue === sev.value;
        const FaceIcon = sev.icon;
        const gradeLabel = grades?.[sev.value] || sev.value;
        
        return (
          <button
            key={sev.value}
            onClick={() => onChange(symptomKey, sev.value)}
            data-testid={`severity-${symptomKey}-${sev.value}`}
            className={`flex flex-col items-center justify-start gap-1.5 py-3 px-1 rounded-2xl transition-all duration-200 ${
              isSelected
                ? 'bg-white shadow-lg scale-105 ring-2'
                : 'bg-white/60 hover:bg-white/80 active:scale-95'
            }`}
            style={{
              ringColor: isSelected ? sev.color : 'transparent',
              minHeight: '72px'
            }}
          >
            <FaceIcon 
              className="w-8 h-8 sm:w-10 sm:h-10 flex-shrink-0"
              style={{ 
                color: sev.color,
                filter: isSelected ? `drop-shadow(0 2px 4px ${sev.color}50)` : 'none'
              }}
              strokeWidth={1.5}
              fill={isSelected ? sev.bgColor : 'none'}
            />
            <span 
              className="text-[9px] sm:text-[11px] font-bold text-center leading-tight line-clamp-2 w-full min-h-[22px] sm:min-h-[26px] flex items-center justify-center"
              style={{ color: isSelected ? sev.color : '#64748b' }}
            >
              {gradeLabel}
            </span>
          </button>
        );
      })}
    </div>
  );
};

// ===========================================
// VITALS INPUT COMPONENT
// ===========================================
const VitalInput = ({ icon: Icon, label, audioKey, fallbackText, value, onChange, placeholder, unit, audioCache, testId, step }) => (
  <div className="patient-card space-y-3">
    <div className="flex items-center justify-between">
      <label className="flex items-center gap-3 text-lg font-extrabold text-slate-800">
        <div className="w-11 h-11 rounded-2xl bg-sky-50 flex items-center justify-center">
          <Icon className="w-5 h-5 text-sky-600" strokeWidth={1.5} />
        </div>
        {label}
      </label>
      <PatientSpeakerButton audioKey={audioKey} audioCache={audioCache} fallbackText={fallbackText} />
    </div>
    <div className="relative">
      <input
        type="number"
        step={step || "1"}
        inputMode={step ? "decimal" : "numeric"}
        value={value || ""}
        onChange={onChange}
        data-testid={testId}
        placeholder={placeholder}
        className="patient-input text-2xl pr-16 h-16 font-extrabold"
      />
      <span className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 text-xl font-medium">{unit}</span>
    </div>
  </div>
);

// ===========================================
// DRAFT PERSISTENCE HELPERS
// ===========================================
const DRAFT_KEY = "hana_checkin_draft";

const saveDraft = (data) => {
  try {
    sessionStorage.setItem(DRAFT_KEY, JSON.stringify({ ...data, savedAt: Date.now() }));
  } catch { /* quota exceeded — silently ignore */ }
};

const loadDraft = () => {
  try {
    const raw = sessionStorage.getItem(DRAFT_KEY);
    if (!raw) return null;
    const draft = JSON.parse(raw);
    // Expire drafts older than 2 hours
    if (Date.now() - draft.savedAt > 2 * 60 * 60 * 1000) {
      sessionStorage.removeItem(DRAFT_KEY);
      return null;
    }
    return draft;
  } catch {
    sessionStorage.removeItem(DRAFT_KEY);
    return null;
  }
};

const clearDraft = () => {
  try { sessionStorage.removeItem(DRAFT_KEY); } catch { return; }
};

const DEFAULT_SYMPTOMS = {
  fatigue: 0, pain: 0, nausea: 0, diarrhea: 0, constipation: 0,
  dyspnea: 0, appetite: 0, neuropathy: 0, mucositis: 0, rash: 0,
  bleeding: 0, edema: 0, perceivedFever: 0,
  cough: 0, joint_pain: 0, hot_flashes: 0, sleep_issues: 0,
  pruritus: 0, infection_signs: 0,
};

// ===========================================
// MAIN COMPONENT
// ===========================================
export default function CheckIn() {
  const { user } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const isSpontaneous = new URLSearchParams(location.search).get("mode") === "spontaneous";

  // Restore draft on mount
  const draft = useRef(loadDraft());

  const [step, setStep] = useState(draft.current?.step || 1);
  const [symptoms, setSymptoms] = useState(draft.current?.symptoms || { ...DEFAULT_SYMPTOMS });
  const [vitals, setVitals] = useState(draft.current?.vitals || {});
  const [observance, setObservance] = useState(draft.current?.observance || {
    treatmentTaken: null,
    missedDoses: null,
    qualityOfLife: null,
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [alreadyDone, setAlreadyDone] = useState(false);
  const audioCache = usePatientAudioCache();
  const [checkinResult, setCheckinResult] = useState(null);
  const [checkinConfig, setCheckinConfig] = useState(draft.current?.checkinConfig || {
    therapeuticClasses: [],
    symptomBundleVersions: ["UNIVERSAL_CORE@1.0"],
    displayedSymptoms: ["fatigue", "pain", "perceivedFever", "nausea", "dyspnea"],
  });

  // Study questionnaire state
  const [pendingQuestionnaires, setPendingQuestionnaires] = useState([]);
  const [currentQIdx, setCurrentQIdx] = useState(0);
  const [questionnaireData, setQuestionnaireData] = useState(null);
  const [qResponses, setQResponses] = useState({});
  const [qStartTime, setQStartTime] = useState(null);
  const [submittingQ, setSubmittingQ] = useState(false);
  const maxStep = pendingQuestionnaires.length > 0 ? 3 : 2;

  // Auto-save draft whenever form data changes
  useEffect(() => {
    saveDraft({ step, symptoms, vitals, observance, checkinConfig });
  }, [step, symptoms, vitals, observance, checkinConfig]);

  // Check if already done today
  useEffect(() => {
    api.get(`/patients/${user.id}/checkins?limit=10`)
      .then(res => {
        const scheduled = (res.data || []).find(c => c.checkinType !== "spontaneous_report");
        if (scheduled) {
          const last = new Date(scheduled.createdAt);
          const today = new Date();
          if (!isSpontaneous && last.toDateString() === today.toDateString()) {
            setAlreadyDone(true);
          }
        }
      });

    // Load pending study questionnaires
    api.get("/studies/my-pending")
      .then(res => {
        setPendingQuestionnaires(res.data || []);
      })
      .catch(() => {});

    api.get(`/patients/${user.id}/checkin-config`, { skipAuthRedirect: true })
      .then(res => {
        setCheckinConfig(res.data);
        const displayed = res.data?.displayedSymptoms || [];
        setSymptoms(prev => {
          const next = { ...prev };
          displayed.forEach(key => {
            if (next[key] === undefined) next[key] = 0;
          });
          return next;
        });
      })
      .catch(() => {});
  }, [user.id, isSpontaneous]);

  const setSymptomValue = (key, value) => {
    setSymptoms(prev => ({ ...prev, [key]: value }));
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setSubmitError(null);
    
    try {
      const formattedVitals = {
        ...vitals,
        bloodPressure: vitals.systole && vitals.diastole ? `${vitals.systole}/${vitals.diastole}` : null,
      };

      const displayedSymptoms = checkinConfig.displayedSymptoms || [];
      const symptomPayload = displayedSymptoms.reduce((acc, key) => {
        acc[key] = symptoms[key] ?? 0;
        return acc;
      }, {});

      const res = await api.post(`/patients/${user.id}/checkin`, {
        checkinType: isSpontaneous ? "spontaneous_report" : "scheduled_checkin",
        symptoms: symptomPayload,
        displayedSymptoms,
        symptomBundleVersions: checkinConfig.symptomBundleVersions || [],
        therapeuticClasses: checkinConfig.therapeuticClasses || [],
        vitals: formattedVitals,
        treatmentTaken: observance.treatmentTaken,
        missedDoses: observance.missedDoses,
        qualityOfLife: observance.qualityOfLife,
      });

      // Success — clear draft
      clearDraft();

      // If there are study questionnaires, go to step 3 instead of finishing
      if (pendingQuestionnaires.length > 0) {
        setStep(3);
        loadQuestionnaire(0);
        setSubmitting(false);
        return;
      }

      // Show result screen
      setCheckinResult(res.data);
    } catch (err) {
      // Detect network / timeout errors vs server errors
      if (!err.response) {
        // Network error or timeout — data is safe in sessionStorage
        setSubmitError("network");
        toast.error(t("checkin.errors.networkLostFull"));
      } else if (err.response.status === 503) {
        setSubmitError("unavailable");
        toast.error(t("checkin.errors.unavailableFull"));
      } else {
        toast.error(err.response?.data?.detail || t("error"));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const loadQuestionnaire = async (idx) => {
    if (idx >= pendingQuestionnaires.length) return;
    const pending = pendingQuestionnaires[idx];
    try {
      const res = await api.get(`/studies/questionnaires/${pending.questionnaireCode}`);
      setQuestionnaireData(res.data);
      setQResponses({});
      setQStartTime(Date.now());
      setCurrentQIdx(idx);
    } catch {
      toast.error(t("checkin.questionnaire.loadError"));
    }
  };

  const setQResponse = (qNum, value) => {
    setQResponses(prev => ({ ...prev, [`q${qNum}`]: value }));
  };

  const handleSubmitQuestionnaire = async () => {
    if (!questionnaireData) return;
    const pending = pendingQuestionnaires[currentQIdx];
    const completionTime = Math.round((Date.now() - qStartTime) / 1000);
    
    setSubmittingQ(true);
    try {
      await api.post(`/studies/enrollments/${pending.enrollmentId}/responses`, {
        questionnaireCode: pending.questionnaireCode,
        visitName: pending.visitName,
        responses: qResponses,
        completionTime,
      });
      toast.success(t("checkin.questionnaire.thanks"));

      // Move to next questionnaire or finish
      if (currentQIdx + 1 < pendingQuestionnaires.length) {
        loadQuestionnaire(currentQIdx + 1);
      } else {
        navigate("/patient/home");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("error"));
    } finally {
      setSubmittingQ(false);
    }
  };

  const skipQuestionnaire = () => {
    if (currentQIdx + 1 < pendingQuestionnaires.length) {
      loadQuestionnaire(currentQIdx + 1);
    } else {
      navigate("/patient/home");
    }
  };

  // Result screen after successful check-in
  if (checkinResult) {
    const level = checkinResult.alertLevel || "VERT";
    const reasons = checkinResult.alertReasons || [];
    const ctx = checkinResult.treatmentContext;
    
    const levelConfig = {
      VERT: {
        bg: "bg-emerald-50", border: "border-emerald-200", icon: "text-emerald-600",
        title: t("checkin.result.titleVert"),
        desc: t("checkin.result.descVert"),
        iconBg: "bg-emerald-100",
      },
      ORANGE: {
        bg: "bg-amber-50", border: "border-amber-200", icon: "text-amber-600",
        title: t("checkin.result.titleOrange"),
        desc: t("checkin.result.descOrange"),
        iconBg: "bg-amber-100",
      },
      ROUGE: {
        bg: "bg-red-50", border: "border-red-200", icon: "text-red-600",
        title: t("checkin.result.titleRouge"),
        desc: t("checkin.result.descRouge"),
        iconBg: "bg-red-100",
      },
    };
    const cfg = levelConfig[level] || levelConfig.VERT;

    return (
      <PatientLayout>
        <div className="max-w-md mx-auto text-center py-8 space-y-6 animate-fade-in-up" data-testid="checkin-result-screen">
          <div className={`w-24 h-24 mx-auto ${cfg.iconBg} rounded-[2rem] flex items-center justify-center shadow-sm`}>
            <CheckCircle className={`w-10 h-10 ${cfg.icon}`} strokeWidth={1.5} />
          </div>
          
          <div>
            <h2 className="text-3xl font-extrabold text-slate-800 leading-tight" data-testid="checkin-result-title">{cfg.title}</h2>
            {cfg.desc && <p className="text-slate-500 text-lg leading-relaxed mt-3 px-4">{cfg.desc}</p>}
          </div>

          {reasons.length > 0 && (
            <div className={`${cfg.bg} ${cfg.border} border rounded-3xl p-5 text-left`} data-testid="checkin-result-reasons">
              <p className="text-sm font-extrabold text-slate-700 mb-2">{t("checkin.result.detailsLabel")}</p>
              {reasons.map((r, i) => (
                <p key={i} className="text-sm text-slate-600 leading-relaxed">- {r}</p>
              ))}
            </div>
          )}

          {ctx && ctx.treatment_day && (
            <div className="bg-white/80 border border-sky-100 rounded-3xl p-4 text-sm text-slate-500" data-testid="checkin-treatment-context">
              {t("checkin.result.treatmentDay")} : <span className="font-medium text-slate-700">{ctx.treatment_day}</span>
              {ctx.cycle_number && <> | {t("checkin.result.cycle")} : <span className="font-medium text-slate-700">{ctx.cycle_number}</span></>}
            </div>
          )}

          <button
            onClick={() => navigate("/patient/home")}
            className="patient-primary-action px-8"
            data-testid="checkin-result-home-btn"
          >
            {t("checkin.result.backHome")}
          </button>
        </div>
      </PatientLayout>
    );
  }

  // Already done today screen
  if (alreadyDone) {
    return (
      <PatientLayout>
        <div className="max-w-md mx-auto text-center py-12 animate-fade-in-up">
          <div className="w-24 h-24 mx-auto bg-emerald-50 rounded-[2rem] flex items-center justify-center mb-6 shadow-sm">
            <CheckCircle className="w-11 h-11 text-emerald-600" strokeWidth={1.5} />
          </div>
          <h2 className="text-3xl font-extrabold text-slate-800 mb-3 leading-tight">{t("checkin.alreadyDone")}</h2>
          <div className="space-y-3 mb-4">
            <button
              onClick={() => {
                setAlreadyDone(false);
                navigate("/patient/checkin?mode=spontaneous", { replace: true });
              }}
              className="patient-primary-action px-6"
              data-testid="checkin-already-done-report-button"
            >
              {t("checkin.reportProblem")}
            </button>
          </div>
          <button
            onClick={() => navigate("/patient/home?skipAutoCheckin=1")}
            className="px-6 py-3 rounded-2xl bg-white border border-slate-200 text-slate-700 font-extrabold"
            data-testid="checkin-already-done-home-btn"
          >
            {t("checkin.backHome")}
          </button>
        </div>
      </PatientLayout>
    );
  }

  const progress = (Math.min(step, maxStep) / maxStep) * 100;
  const displayedSymptomKeys = (checkinConfig.displayedSymptoms || []).filter(Boolean).slice(0, 8);
  const displayedSymptomConfigs = SYMPTOMS_KEYS.filter(s => displayedSymptomKeys.includes(s.key));
  const setNothingToReport = () => {
    setSymptoms(prev => {
      const next = { ...prev };
      displayedSymptomKeys.forEach(key => { next[key] = 0; });
      return next;
    });
    setStep(2);
  };

  // Observance and QoL options
  const TREATMENT_OPTIONS = [
    { value: "Oui", label: t("checkin.observance.yes"), color: "bg-green-100 text-green-700 border-green-300" },
    { value: "Non", label: t("checkin.observance.no"), color: "bg-red-100 text-red-700 border-red-300" },
  ];

  const MISSED_OPTIONS = [
    { value: "Aucun", label: t("checkin.observance.noMissed"), color: "bg-green-100 text-green-700 border-green-300" },
    { value: "Occasionnels", label: t("checkin.observance.occasional"), color: "bg-amber-100 text-amber-700 border-amber-300" },
    { value: "Fréquents", label: t("checkin.observance.frequent"), color: "bg-red-100 text-red-700 border-red-300" },
  ];

  const QOL_OPTIONS = [
    { value: "Bonne", label: t("checkin.qol.good"), color: "bg-green-100 text-green-700 border-green-300" },
    { value: "Moyenne", label: t("checkin.qol.average"), color: "bg-yellow-100 text-yellow-700 border-yellow-300" },
    { value: "Altérée", label: t("checkin.qol.poor"), color: "bg-orange-100 text-orange-700 border-orange-300" },
    { value: "Très altérée", label: t("checkin.qol.veryPoor"), color: "bg-red-100 text-red-700 border-red-300" },
  ];

  return (
    <PatientLayout>
      <div className="space-y-6 max-w-2xl mx-auto">
        {/* Header */}
        <div className="animate-fade-in-up flex items-center justify-between gap-3">
          <h1 className="patient-soft-title" data-testid="checkin-page-title">{isSpontaneous ? t("checkin.reportProblem") : t("checkin.title")}</h1>
          <button
            type="button"
            onClick={() => navigate("/patient/home")}
            className="shrink-0 inline-flex items-center gap-2 px-4 py-2 rounded-2xl bg-white border border-sky-100 text-sky-800 font-extrabold text-sm hover:bg-sky-50 transition-colors"
            data-testid="checkin-back-home-top"
          >
            <Home className="w-4 h-4" strokeWidth={2} />
            {t("checkin.backHomeTop")}
          </button>
        </div>

        {/* Progress Bar */}
        <div className="animate-fade-in-up stagger-1 patient-card bg-white/75">
          <div className="flex items-center justify-between text-sm text-slate-500 mb-3 font-extrabold">
            <span>{t("checkin.step")} {Math.min(step, maxStep)} {t("checkin.of")} {maxStep}</span>
            <span>{Math.round(progress)}%</span>
          </div>
          <div className="h-3 bg-sky-50 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-sky-500 to-emerald-400 rounded-full transition-all duration-500"
              style={{ width: `${progress}%` }}
            />
          </div>

          <div className="flex justify-between mt-5">
            {[
              { n: 1, labelKey: "checkin.pageSymptoms", icon: AlertCircle },
              { n: 2, labelKey: "checkin.pageVitals", icon: Activity },
              ...(pendingQuestionnaires.length > 0 ? [{ n: 3, labelKey: "checkin.questionnaire.label", icon: ClipboardList }] : []),
            ].map(s => (
              <div
                key={s.n}
                className={`flex items-center gap-2 text-sm font-extrabold ${step >= s.n ? "text-sky-700" : "text-slate-400"}`}
              >
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-all ${
                  step >= s.n ? "bg-sky-600 text-white" : "bg-slate-100 text-slate-400"
                }`}>
                  {step > s.n ? <CheckCircle className="w-4 h-4" strokeWidth={2} /> : s.n}
                </div>
                <span className="hidden sm:inline">{s.labelKey ? t(s.labelKey) : s.label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* ============================================= */}
        {/* STEP 1: Symptoms - Score 0-3 Scale */}
        {/* ============================================= */}
        {step === 1 && (
          <div className="space-y-4 animate-fade-in-up">
            <div className="patient-card bg-sky-50/90 border-sky-100">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1">
                  <p className="text-xl font-extrabold text-sky-800 leading-snug">
                    {t("checkin.symptomsIntro")}
                  </p>
                  <p className="text-sm text-sky-700/75 mt-1 leading-relaxed">
                    {t("checkin.symptomsIntroDesc")}
                  </p>
                </div>
                <PatientSpeakerButton audioKey="step1_intro" audioCache={audioCache} fallbackText={t("checkin.audioMessages.step1_intro")} />
              </div>
            </div>

            {/* Severity Legend */}
            <div className="flex items-center justify-center gap-3 sm:gap-6 py-3 px-4 bg-white/80 border border-sky-100 rounded-3xl text-xs text-slate-600 shadow-sm">
              {SEVERITY_LEVELS.map(sev => {
                const FaceIcon = sev.icon;
                return (
                  <span key={sev.value} className="flex items-center gap-1.5 font-bold">
                    <FaceIcon className="w-5 h-5" style={{ color: sev.color }} strokeWidth={1.5} />
                    <span className="hidden sm:inline">{t(`checkin.severity.${sev.value}`)}</span>
                    <span className="sm:hidden">{sev.value}</span>
                  </span>
                );
              })}
            </div>

            <button
              type="button"
              onClick={setNothingToReport}
              className="w-full py-5 px-6 bg-emerald-600 text-white rounded-3xl font-extrabold text-xl flex flex-col items-center justify-center gap-1 hover:bg-emerald-500 transition-colors shadow-lg shadow-emerald-600/25"
              data-testid="checkin-nothing-to-report-button"
            >
              <span className="flex items-center gap-2"><CheckCircle className="w-6 h-6" strokeWidth={2.2} />{t("checkin.nothingToReport")}</span>
              <span className="text-sm font-semibold text-emerald-50/90">{t("checkin.nothingToReportHint")}</span>
            </button>

            {/* Symptom Cards */}
            {displayedSymptomConfigs.map((s) => {
              const currentValue = symptoms[s.key];
              const Icon = s.icon;
              const grades = t(`checkin.grades.${s.key}`) || ["0", "1", "2", "3"];
              return (
                <div key={s.key} className="patient-card space-y-3" data-testid={`symptom-card-${s.key}`}>
                  <div className="flex items-center gap-3">
                    {/* Medical Icon */}
                    <div className="w-11 h-11 sm:w-12 sm:h-12 rounded-2xl bg-sky-50 flex items-center justify-center">
                      <Icon className="w-5 h-5 sm:w-6 sm:h-6 text-sky-600" strokeWidth={1.5} />
                    </div>
                    {/* Label */}
                    <p className="text-lg sm:text-xl font-extrabold text-slate-800 flex-1 leading-snug">
                      {t(`checkin.symptoms.${s.key}`)}
                    </p>
                    {/* Speaker button */}
                    <PatientSpeakerButton audioKey={s.key} audioCache={audioCache} fallbackText={t(`checkin.audioMessages.${s.key}`)} />
                  </div>

                  {/* Severity Selector */}
                  <SeveritySelector 
                    symptomKey={s.key}
                    currentValue={currentValue}
                    onChange={setSymptomValue}
                    grades={grades}
                  />
                </div>
              );
            })}
          </div>
        )}

        {/* ============================================= */}
        {/* STEP 2: Vitals */}
        {/* ============================================= */}
        {step === 2 && (
          <div className="space-y-4 animate-fade-in-up">
            <div className="patient-card bg-emerald-50/90 border-emerald-100">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1">
                  <p className="text-lg text-emerald-800 leading-relaxed" data-testid="checkin-vitals-title">
                    <strong>{t("checkin.vitalsOptional")}</strong>
                  </p>
                  <p className="text-sm text-emerald-700/80 mt-1">{t("checkin.pageVitalsDesc")}</p>
                </div>
                <PatientSpeakerButton audioKey="step2_intro" audioCache={audioCache} fallbackText={t("checkin.audioMessages.step2_intro")} />
              </div>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={submitting}
                className="mt-4 w-full py-4 px-5 bg-white border-2 border-emerald-600 text-emerald-800 rounded-3xl font-extrabold text-base flex items-center justify-center gap-2 hover:bg-emerald-100 transition-colors disabled:opacity-50"
                data-testid="checkin-skip-vitals-submit"
              >
                <CheckCircle className="w-5 h-5" strokeWidth={2} />
                {submitting ? t("checkin.sending") : t("checkin.skipVitalsSend")}
              </button>
            </div>

            {/* Temperature */}
            <VitalInput 
              icon={Thermometer}
              label={t("checkin.vitals.temperature")}
              audioKey="temperature"
              fallbackText={t("checkin.audioMessages.temperature")}
              value={vitals.temperature}
              onChange={e => setVitals(prev => ({ ...prev, temperature: e.target.value }))}
              placeholder="37.0"
              unit="°C"
              audioCache={audioCache}
              testId="vital-temperature"
              step="0.1"
            />

            {/* Heart Rate */}
            <VitalInput 
              icon={Heart}
              label={t("checkin.vitals.heartRate")}
              audioKey="heartRate"
              fallbackText={t("checkin.audioMessages.heartRate")}
              value={vitals.heartRate}
              onChange={e => setVitals(prev => ({ ...prev, heartRate: e.target.value }))}
              placeholder="70"
              unit="bpm"
              audioCache={audioCache}
              testId="vital-heartRate"
            />

            {/* SpO2 */}
            <VitalInput 
              icon={Activity}
              label={t("checkin.vitals.spo2")}
              audioKey="spo2"
              fallbackText={t("checkin.audioMessages.spo2")}
              value={vitals.spo2}
              onChange={e => setVitals(prev => ({ ...prev, spo2: e.target.value }))}
              placeholder="98"
              unit="%"
              audioCache={audioCache}
              testId="vital-spo2"
            />

            {/* Weight */}
            <VitalInput 
              icon={Scale}
              label={t("checkin.vitals.weight")}
              audioKey="weight"
              fallbackText={t("checkin.audioMessages.weight")}
              value={vitals.weight}
              onChange={e => setVitals(prev => ({ ...prev, weight: e.target.value }))}
              placeholder="70"
              unit="kg"
              audioCache={audioCache}
              testId="vital-weight"
              step="0.1"
            />

            {/* Blood Pressure */}
            <div className="patient-card space-y-3">
              <div className="flex items-center justify-between">
                <label className="flex items-center gap-3 text-lg font-extrabold text-slate-800">
                  <div className="w-11 h-11 rounded-2xl bg-sky-50 flex items-center justify-center">
                    <Activity className="w-5 h-5 text-sky-600" strokeWidth={1.5} />
                  </div>
                  {t("checkin.vitals.bloodPressure")}
                </label>
                <PatientSpeakerButton audioKey="bloodPressure" audioCache={audioCache} fallbackText={t("checkin.audioMessages.bloodPressure")} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="relative">
                  <input
                    type="number"
                    inputMode="numeric"
                    value={vitals.systole || ""}
                    onChange={e => setVitals(prev => ({ ...prev, systole: e.target.value }))}
                    data-testid="vital-systole"
                    placeholder="120"
                    className="patient-input text-xl pr-12 h-14 font-extrabold"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">
                    {t("checkin.vitals.systole")}
                  </span>
                </div>
                <div className="relative">
                  <input
                    type="number"
                    inputMode="numeric"
                    value={vitals.diastole || ""}
                    onChange={e => setVitals(prev => ({ ...prev, diastole: e.target.value }))}
                    data-testid="vital-diastole"
                    placeholder="80"
                    className="patient-input text-xl pr-12 h-14 font-extrabold"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">
                    {t("checkin.vitals.diastole")}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Step 3: Study Questionnaire (conditional) */}
        {step === 3 && questionnaireData && (
          <div className="space-y-4 animate-fade-in-up">
            {/* Study header */}
            <div className="patient-card bg-sky-50/90 border-sky-100">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-2xl bg-white flex items-center justify-center shadow-sm">
                  <ClipboardList className="w-5 h-5 text-sky-600" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="font-extrabold text-sky-800">
                    {pendingQuestionnaires[currentQIdx]?.studyName}
                  </p>
                  <p className="text-sm text-sky-700">
                    {questionnaireData.name} - {pendingQuestionnaires[currentQIdx]?.visitName}
                  </p>
                </div>
              </div>
              <p className="text-base text-sky-700 mt-3 leading-relaxed">
                {t("checkin.questionnaire.introPrefix")} {questionnaireData.estimatedMinutes} {t("checkin.questionnaire.introSuffix")}
              </p>
            </div>

            {/* Questionnaire items */}
            {questionnaireData.items?.map((item, idx) => {
              const responseType = item.responseType;
              const options = responseType === "likert_4"
                ? questionnaireData.responseOptions?.likert_4
                : responseType === "likert_7"
                  ? questionnaireData.responseOptions?.likert_7
                  : responseType === "eq5d_5"
                    ? questionnaireData.responseOptions?.eq5d_5?.[item.subscale]
                    : null;
              const isVAS = responseType === "vas_100";

              return (
                <div key={item.number} className="patient-card space-y-3" data-testid={`q-item-${item.number}`}>
                  <p className="text-lg font-extrabold text-slate-800 leading-relaxed">
                    {idx + 1}. {item.text}
                  </p>
                  {options && (
                    <div className="space-y-2">
                      {options.map((opt) => (
                        <button
                          key={opt.value}
                          onClick={() => setQResponse(item.number, opt.value)}
                          data-testid={`q${item.number}-opt-${opt.value}`}
                          className={`w-full text-left px-4 py-4 rounded-2xl text-base font-bold transition-all ${
                            qResponses[`q${item.number}`] === opt.value
                              ? "bg-sky-600 text-white shadow-md"
                              : "bg-sky-50/60 text-slate-700 hover:bg-sky-50"
                          }`}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  )}
                  {isVAS && (
                    <div className="space-y-2">
                      <input
                        type="range"
                        min="0"
                        max="100"
                        step="1"
                        value={qResponses[`q${item.number}`] || 50}
                        onChange={e => setQResponse(item.number, parseInt(e.target.value))}
                        data-testid={`q${item.number}-vas`}
                        className="w-full h-3 accent-sky-600"
                      />
                      <div className="flex justify-between text-sm text-slate-500">
                        <span>{t("checkin.questionnaire.vasMin")}</span>
                        <span className="text-2xl font-extrabold text-sky-600">{qResponses[`q${item.number}`] || 50}</span>
                        <span>{t("checkin.questionnaire.vasMax")}</span>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}

            {/* Questionnaire navigation */}
            <div className="flex gap-3 pt-4">
              <button
                onClick={skipQuestionnaire}
                className="flex-1 py-4 px-6 bg-white text-slate-500 rounded-3xl font-extrabold text-lg flex items-center justify-center gap-2 border border-sky-100"
                data-testid="btn-skip-q"
              >
                {t("checkin.questionnaire.skip")}
              </button>
              <button
                onClick={handleSubmitQuestionnaire}
                disabled={submittingQ}
                className="flex-1 py-4 px-6 bg-sky-600 text-white rounded-3xl font-extrabold text-lg flex items-center justify-center gap-2 hover:bg-sky-500 disabled:opacity-50 shadow-lg shadow-sky-600/20"
                data-testid="btn-submit-q"
              >
                {submittingQ ? (
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <>
                    <CheckCircle className="w-5 h-5" strokeWidth={2} />
                    {t("checkin.questionnaire.send")}
                  </>
                )}
              </button>
            </div>
          </div>
        )}

        {/* Navigation Buttons (Steps 1-2 only) */}
        {step <= 2 && (
        <div className="space-y-3 pt-4 animate-fade-in-up stagger-2">
          {/* Retry banner on submit error */}
          {submitError && step === 2 && (
            <div className="flex items-center gap-3 p-4 bg-rose-50 border border-rose-100 rounded-3xl" data-testid="checkin-submit-error">
              <AlertCircle className="w-6 h-6 text-red-500 flex-shrink-0" strokeWidth={1.5} />
              <div className="flex-1">
                <p className="text-sm font-extrabold text-rose-800">
                  {submitError === "network" ? t("checkin.errors.networkLost") : t("checkin.errors.unavailable")}
                </p>
                <p className="text-xs text-rose-600 mt-0.5">
                  {t("checkin.errors.retryHint")}
                </p>
              </div>
              <button
                onClick={handleSubmit}
                disabled={submitting}
                className="px-4 py-2 bg-rose-600 text-white rounded-2xl text-sm font-extrabold hover:bg-rose-500 transition-colors disabled:opacity-50"
                data-testid="btn-retry-submit"
              >
                {submitting ? "..." : t("checkin.errors.retry")}
              </button>
            </div>
          )}

          <div className="flex gap-3">
          {step > 1 && (
            <button
              onClick={() => setStep(s => s - 1)}
              className="flex-1 py-4 px-6 bg-white text-slate-600 rounded-3xl font-extrabold text-lg flex items-center justify-center gap-2 hover:bg-sky-50 transition-colors border border-sky-100"
              data-testid="btn-back"
            >
              <ArrowLeft className="w-5 h-5" strokeWidth={2} />
              {step === 2 ? t("checkin.backToSymptoms") : t("back")}
            </button>
          )}

          {step < 2 ? (
            <button
              onClick={() => setStep(s => s + 1)}
              className="flex-1 py-4 px-6 bg-sky-600 text-white rounded-3xl font-extrabold text-lg flex items-center justify-center gap-2 hover:bg-sky-500 transition-colors shadow-lg shadow-sky-600/20"
              data-testid="btn-next"
            >
              {t("next")}
              <ArrowRight className="w-5 h-5" strokeWidth={2} />
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={submitting}
              className="flex-1 py-4 px-6 bg-emerald-600 text-white rounded-3xl font-extrabold text-lg flex items-center justify-center gap-2 hover:bg-emerald-500 transition-colors disabled:opacity-50 shadow-lg shadow-emerald-600/20"
              data-testid="btn-submit"
            >
              {submitting ? (
                <>
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  {t("checkin.sending")}
                </>
              ) : (
                <>
                  <CheckCircle className="w-5 h-5" strokeWidth={2} />
                  {t("checkin.finish")}
                </>
              )}
            </button>
          )}
          </div>
        </div>
        )}
      </div>
    </PatientLayout>
  );
}
